import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql, navigate, Link } from 'gatsby';
import { useSelector, useDispatch } from 'react-redux';
import { PayPalMessages, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { Badge, Collapse, Container, Row, Column, List, Button, Modal, Alert, Carousel, TextInput, Icon } from '@pella/sash-framework';
import { getProductReviews, resetReviews } from '../../actions/ratings-reviews-actions';
import { getServiceInfo, resetMarket } from '../../actions/location-actions';
import { Layout, SEO, Breadcrumbs, Image, LoadingOverlay, CTA } from '../../components';
import { setSessionStorage, getLocalStorage, getCookie, monetateDataLayer } from '../../components/utilities/functions';
import { setTag, getCurrentScope, setContext, captureMessage } from '@sentry/gatsby';
import { Product } from '../../components/common/structured-data';
import { useOktaAuth } from '../../hooks/use-okta-auth';
import { useCheckout } from '../../hooks/use-checkout';
import { useAccount } from '../../hooks/use-account';
import { useCart } from '../../hooks/use-cart';

import PDPTabs from '../../components/template/pdp-static/tabs';
import ReviewsSlider from '../../components/common/reviews-slider';
import StarRatings from '../../components/common/star-ratings';
import FAQ from '../../components/common/faq';
import ImageSlider from '../../components/template/pdp-static/image-slider';
import DynamicComponent from '../../components/common/dynamic-component';
import Price from '../../components/page/shop/common/price';
import CardSection from '../../components/common/card-section';
import EcomCardSlider from '../../components/common/ecom-card-slider';
import AddressValidation from '../../components/common/address-validation/v3';
import TopNav from '../../components/template/pdp-static/top-nav';
import ImageBanner from '../../components/common/image-banner';
import styles from './index.module.scss';
import CTARibbon from '../../components/common/cta-ribbon';
import { isValidArray, isValidPostal } from '../../components/utilities/validations';
import NonTransactableOptions from '../../components/template/pdp-static/non-transactable-options';
import { usePDPConfig } from '../../hooks/use-pdp-config';
import { useLocation } from '../../context/LocationContext';
import queryString from 'query-string';
import ADMProduct from '../../components/template/document-landing/adm-product';
import { HoverEmbed } from '../../components/third-party/hover';
import Feature from '../../components/common/feature';
import { useProduct } from '../../hooks/use-product';

const PDPStatic = (props) => {
	const pageData = props.data.contentstackStaticPdp;
	const configData = usePDPConfig();
	const locationConfig = props.data.contentstackLocationConfig;
	const errorData = configData?.errors;
	const reduxDispatch = useDispatch();
	const reviews = useSelector((state) => state.reviews);
	const locations = useSelector((state) => state.locations);
	const [tile, setTile] = useState(pageData.product_tile?.[0]?.manual ? pageData.product_tile[0] : props.data.productTiles);
	const [isEcommerce, setIsEcommerce] = useState(false);
	const [isPartsOnly, setIsPartsOnly] = useState(false);
	const [isPro, setIsPro] = useState(null);
	const [navItems, setNavItems] = useState([]);
	const [checkDynamic, setCheckDynamic] = useState({});
	const [currentZip, setCurrentZip] = useState(null);
	const [loading, setLoading] = useState(true);
	const [modalOpen, setModalOpen] = useState(false);
	const [zipValue, setZipValue] = useState(null);
	const [modalHasError, setModalHasError] = useState(false);
	const [modalErrorMessage, setModalErrorMessage] = useState('');
	const [hasLocationPostalCode, setHasLocationPostalCode] = useState(false);
	const [didConfirm, setDidConfirm] = useState(false);
	const [useOutOfUSError, setUseOutOfUSError] = useState(false);
	const [isEast, setIsEast] = useState(true);
	const [didSelectPro, setDidSelectPro] = useState(null);
	const [showPricingTooltip, setShowPricingTooltip] = useState(false);
	const [ratings, setRatings] = useState({});
	const productHeading = pageData?.overview?.product_heading?.replace(/<\/?span[^>]*>/g, '');
	const CONFIG_URL = `/shop/configure/?id=${tile?.backend_name}&type=${tile?.type}&name=${productHeading}&heading=${tile?.header} ${tile?.display_name}&pdpURL=${typeof window != 'undefined' && window.location.origin + pageData?.url}&isPro=${isPro}`;
	const [{}, dispatch] = usePayPalScriptReducer();
	const [showShippingTooltip, setShowShippingTooltip] = useState(false);
	const [addressModalOpen, setAddressModalOpen] = useState(false);
	const [addressOutput, setAddressOutput] = useState({});
	const [addressComplete, setAddressComplete] = useState(false);
	const [addressModified, setAddressModified] = useState(false);
	const [customer, setCustomer] = useState({});
	const [customerRecord, setCustomerRecord] = useState({});
	const [postalCode, setPostalCode] = useState('');
	const [deliveryLocation, setDeliveryLocation] = useState({});
	const [pickupLocation, setPickupLocation] = useState({});
	const [errors, setErrors] = useState({});
	const [addressSubmitted, setAddressSubmitted] = useState(false);
	const [add1, setAdd1] = useState('');
	const [addCity, setAddCity] = useState('');
	const [addState, setAddState] = useState('');
	const [addPostal, setAddPostal] = useState('');
	const [submittedOnce, setSubmittedOnce] = useState(false);
	const [update, setUpdate] = useState(true);
	const [pickupError, setPickupError] = useState(false);
	const [deliveryError, setDeliveryError] = useState(false);
	const { profile } = useOktaAuth();
	const { fetchCustomer } = useAccount();
	const { getAddress } = useCheckout();
	const { getDeliveryOptionDetails } = useCart();
	const { setLocation } = useLocation();
	const { fetchProductTile } = useProduct();
	const isProjectButton = isPartsOnly || (!tile.available_online && !tile.manual) || (!isPro && tile?.price?.consumer?.amount === 0);

	useEffect(() => {
		const userPostalCode = getLocalStorage('UserPostalCode');
		const userLocation = getLocalStorage('UserLocation');
		const region_id = queryString.parse(window.location.search).region_id;
		const zip = userPostalCode || userLocation?.postal_code || region_id;

		setCurrentZip(zip);
		setPostalCode(zip);
		setLocation(zip);
		if (userPostalCode) {
			setZipValue(zip);
			setHasLocationPostalCode(true);
		}

		if (getCookie('CustomerType') === 'Professional') setDidSelectPro(true);

		getCustomerData();

		// eslint-disable-next-line react-hooks/exhaustive-deps -- this will be fixed when we refactor
	}, []);

	const getCustomerData = async () => {
		const customer = await fetchCustomer();
		if (customer?.Customer && Object.keys(customer.Customer).length > 0 && !customer?.error) {
			setCustomer(customer.Customer);
			if (customer.Customer.primary_address !== null) {
				const address = await getAddress(customer.Customer.id, customer.Customer.primary_address);
				if (Object.keys(address?.Address).length > 0 && !address?.error) {
					setCustomerRecord(address.Address);
					setLoading(false);
				} else {
					setTag('Pella Component', 'Checkout', 'API Transaction ID', address?.REQUEST_ID);
					getCurrentScope().setTransactionName('getAddress');
					setContext('Error', {
						message: address?.error?.message,
						details: address?.error?.details,
						customerID: customer.Customer.id,
					});
					captureMessage(address.error?.message || 'Unknown Error');
					setErrors(address.error?.message);
					setLoading(false);
				}
			} else {
				setLoading(false);
			}
		} else if (customer?.error) {
			setTag('Pella Component', 'Checkout', 'API Transaction ID', customer?.REQUEST_ID);
			getCurrentScope().setTransactionName('fetchCustomer');
			setContext('Error', {
				message: customer.error?.message,
				details: customer.error?.details,
				customerID: customer?.Customer?.id,
			});
			captureMessage(customer.error?.message || 'Unknown Error');
			setErrors(customer.error?.message);
			setLoading(false);
		}
	};

	useEffect(() => {
		if (!tile?.backend_name || tile?.manual) return;

		(async () => {
			const productTile = await fetchProductTile(tile.backend_name);
			if (productTile?.data?.success) {
				setTile((current) => ({
					...current,
					...productTile.data,
				}));
				monetateDataLayer('addProductDetails', [{ productId: tile.backend_name }]);
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- stingify to prevent infinite loop
	}, [JSON.stringify(tile), isPro, JSON.stringify(locations.market)]);

	useEffect(() => {
		if (update) {
			setUpdate(false);
		}
	}, [update]);

	useEffect(() => {
		if (Object.keys(customerRecord).length > 0) {
			setLoading(true);
			if (customerRecord.line_1 && customerRecord.city && customerRecord.state && customerRecord.postcode) {
				setAddressOutput({
					address1: customerRecord.line_1,
					city: customerRecord.city,
					state: customerRecord.state,
					postalCode: customerRecord.postcode,
				});
				setAdd1(customerRecord.line_1);
				setAddCity(customerRecord.city);
				setAddState(customerRecord.state);
				setAddPostal(customerRecord.postcode);
				setAddressComplete(true);
				setAddressSubmitted(true);
				setSubmittedOnce(true);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [customerRecord]);

	useEffect(() => {
		const findWarehouseLocations = async (city, postalCode, state, address1) => {
			setErrors({});
			// Check for branch region to see if west coast
			dispatch(getServiceInfo(postalCode));
			const addressData = {
				line_1: address1,
				city: city,
				state: state,
				postcode: postalCode,
				country: 'US',
			};
			const locations = await getDeliveryOptionDetails(addressData);
			if (!locations?.delivery_location?.message) {
				setDeliveryLocation(locations?.delivery_location);
				setDeliveryError(false);
			} else {
				setDeliveryError(true);
			}
			if (!locations?.pickup_location?.message) {
				setPickupLocation(locations?.pickup_location);
				setPickupError(false);
			} else {
				setPickupError(true);
			}
			if (locations.error) {
				setTag('Pella Component', 'CheckoutFulfillment', 'API Transaction ID', locations?.REQUEST_ID);
				getCurrentScope().setTransactionName('getDeliveryOptionDetails');
				setContext('Error', {
					message: locations?.error?.message,
					details: locations?.error?.details,
				});
				captureMessage(locations.error?.message || 'Unknown Error');
				setErrors(locations.error?.message);
			}
			setSubmittedOnce(true);
			setLoading(false);
			setUpdate(true);
		};
		if (isValidPostal(addPostal) && addCity && addState && add1 && addressSubmitted === true) {
			setLoading(true);
			findWarehouseLocations(addCity, addPostal, addState, add1);
			setAddressSubmitted(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [addressSubmitted, addressOutput, dispatch, setDeliveryLocation, setPickupLocation, addCity, addPostal, addState, add1]);

	useEffect(() => {
		if (reviews && pageData?.product_tile?.[0]?.ratings?.[0]?.ratings_code && reviews[pageData.product_tile[0].ratings[0].ratings_code]) {
			setRatings({ ...reviews, productRatings: reviews[pageData.product_tile[0].ratings[0].ratings_code] });
		}
	}, [reviews, pageData]);

	useEffect(() => {
		dispatch({
			type: 'setLoadingStatus',
			value: 'pending',
		});
	}, [dispatch]);

	useEffect(() => {
		if (!loading && pageData && tile) {
			const price = tile.price?.professional && isPro ? (tile.price.professional.amount / 100).toFixed(2) : tile.price?.consumer?.amount && !isPro ? (tile.price.consumer.amount / 100).toFixed(2) : '0.00';

			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: 'productDetail',
				ecommerce: {
					detail: {
						products: [
							{
								id: tile.mpn || tile.backend_name,
								currency: 'USD',
								price: price,
								name: tile.backend_name,
								category: tile.display_name,
							},
						],
					},
				},
			});
		}
	}, [isPro, loading, pageData, tile]);

	useEffect(() => {
		if (isEcommerce !== locations.ecommerce && typeof locations.ecommerce === 'boolean') {
			setIsEcommerce(locations.ecommerce);
		}
		if (isPartsOnly !== locations.parts_only && typeof locations.parts_only === 'boolean') {
			setIsPartsOnly(locations.parts_only);
		}
		const userPostalCode = getLocalStorage('UserPostalCode');
		if (userPostalCode && userPostalCode !== currentZip) {
			setCurrentZip(userPostalCode);
		} else if (locations?.market?.postal_code && locations.market.postal_code !== currentZip) {
			setCurrentZip(locations.market.postal_code);
		}
		if (locations?.service_info?.branch_region === 'west') {
			setIsEast(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [isEcommerce, locations.ecommerce, locations.parts_only, JSON.stringify(locations.market), JSON.stringify(locations.service_info)]);

	useEffect(() => {
		if (!didConfirm) return;
		if (typeof locations.ecommerce !== 'boolean') return;

		if (locations.ecommerce) {
			navigate(CONFIG_URL);
		} else {
			setIsEcommerce(false);
			setIsPartsOnly(false);
			setUseOutOfUSError(true);
		}
	}, [didConfirm, locations.ecommerce, CONFIG_URL]);

	useEffect(() => {
		if (profile.isPro && !isPro) {
			setIsPro(true);
		} else if (!profile.isPro && (isPro || isPro === null)) {
			setIsPro(false);
		}
	}, [profile.isPro, isPro]);

	useEffect(() => {
		if (pageData?.product_tile?.[0]?.ratings?.[0]?.ratings_code) {
			reduxDispatch(getProductReviews(pageData?.product_tile?.[0]?.ratings?.[0]?.ratings_code));
		} else {
			reduxDispatch(resetReviews());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [pageData]);

	useEffect(() => {
		if (!configData?.group?.ctas?.[0]) return;

		let arr = configData.group.ctas;
		if (!isValidArray(reviews?.Reviews)) {
			const filteredArr = configData.group.ctas.filter((item) => !item.link?.title?.toLowerCase().includes('reviews'));
			arr = filteredArr;
		}
		if (!pageData.modular_blocks.find((block) => block.embed)) {
			const filteredArr = arr.filter((item) => item.link?.href !== '#hover');
			arr = filteredArr;
		}
		setNavItems(arr);
	}, [pageData, configData, reviews]);

	useEffect(() => {
		if ((pageData && isPro != null && isEcommerce != null, isEast != null && tile)) {
			setLoading(false);
		}
	}, [loading, isEcommerce, isPro, isEast, pageData, tile]);

	const checkDynamicPanels = (type, active) => {
		setCheckDynamic({ ...checkDynamic, [type]: active });
	};

	const openModal = () => {
		reduxDispatch(resetMarket());
		setModalOpen(true);
		setModalHasError(false);
		setZipValue('');
	};

	const closeModal = () => {
		setLocation(currentZip);
		setModalOpen(false);
		setModalHasError(false);
		setZipValue('');
	};

	const handleChange = (e) => {
		setModalHasError(false);
		setZipValue(e.target.value);
	};

	const confirmZIPCode = (event) => {
		event.preventDefault();
		const validZip = isValidPostal(zipValue);
		if (!validZip) {
			setModalHasError(true);
			setModalErrorMessage('Please enter a valid ZIP code.');
		} else {
			setModalOpen(false);
			setLocation(zipValue);
			setDidConfirm(true);
		}
	};

	const closeErrorAlert = () => {
		setZipValue('');
		setModalHasError(false);
		setModalErrorMessage('');
	};

	const heroButtonClick = (location, action) => {
		const backend_name = pageData.product_tile?.[0]?.backend_name || tile?.backend_name;
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: `${action}Click`,
			ecommerce: {
				click: {
					products: [
						{
							id: pageData.product_tile?.[0]?.mpn || backend_name,
							name: backend_name,
						},
					],
				},
			},
		});

		if (!location && (action === 'configure' || action === 'project')) {
			openModal();
		} else if (location && (action === 'configure' || action === 'project')) {
			navigate(CONFIG_URL);
		}
	};

	const submitAddress = () => {
		if (addressComplete && (addressOutput.city !== '' || addressOutput.postalCode !== '' || addressOutput.state !== '' || addressOutput.address1 !== '')) {
			setAddressSubmitted(true);
			setAdd1(addressOutput.address1);
			setAddCity(addressOutput.city);
			setAddState(addressOutput.state);
			setAddPostal(addressOutput.postalCode);
			setSessionStorage('pdpAddress1', addressOutput.address1, false);
			setSessionStorage('pdpAddCity', addressOutput.city, false);
			setSessionStorage('pdpAddState', addressOutput.state, false);
			setSessionStorage('pdpAddPostal', addressOutput.postalCode, false);
			setSessionStorage('pdpAddressSet', true, false);
			closeAddressModal();
		} else {
			setModalHasError(true);
			setModalErrorMessage('Please enter a complete and valid address');
		}
	};

	const onPricingInfoClick = () => setShowPricingTooltip(!showPricingTooltip);

	const onShippingTooltipClick = () => setShowShippingTooltip(!showShippingTooltip);

	const closeAddressModal = () => setAddressModalOpen(false);

	const openAddressModal = () => setAddressModalOpen(true);

	const getImageUrls = () => {
		return [
			...(pageData.overview.cob?.[0]?.image?.url ? [pageData.overview.cob[0].image.url] : []),
			...(pageData.overview.beauty_images?.reduce((urls, imageObj) => {
				if (imageObj.image?.url) {
					urls.push(imageObj.image.url);
				}
				return urls;
			}, []) ?? []),
		];
	};

	return (
		<Layout
			id='template-PDPStatic'
			pageTitle={pageData.seo?.title}
			pageType={pageData.seo.page_type}
			topLevelClass={styles.pdp}
		>
			{isValidArray(navItems) && (
				<TopNav
					navTitle={`<span>${pageData?.overview?.product_heading}</span>`}
					linksArray={navItems.map((item) => item)}
				>
					{isEcommerce && tile && (tile.available_online || (!tile.available_online && !tile.manual)) && ((isEast && tile.east_only) || !tile.east_only) && tile.backend_name && (
						<Button
							size='sm'
							color='primary'
							id={isProjectButton ? 'speedcurve-build-and-add-to-project' : 'speedcurve-build-and-add-to-cart'}
							onClick={() => heroButtonClick(hasLocationPostalCode ? `/shop/configure/?id=${tile.backend_name}&type=${tile.type}&name=${productHeading}&heading=${tile.header} ${tile.display_name}&pdpURL=${typeof window != 'undefined' && window.location.href}&isPro=${isPro}` : false, isProjectButton ? 'project' : 'configure')}
							dangerouslySetInnerHTML={{ __html: isProjectButton ? `Build + Add to Project` : `Build + Add to Cart` }}
						/>
					)}
				</TopNav>
			)}
			{pageData.breadcrumb && (
				<Breadcrumbs
					data={pageData.breadcrumb}
					currentUrl={pageData.url}
					className={`${styles.pdpBreadcrumbs} mb-4 md:mb-6`}
				/>
			)}
			{loading && <LoadingOverlay />}
			<Container>
				{!isEcommerce && useOutOfUSError && errorData && (
					<Alert
						className='mb-4 text-center'
						type='error'
						hideClose
					>
						<div
							dangerouslySetInnerHTML={{
								__html: errorData?.find((item) => item.non_field_id === 'non US error')?.error,
							}}
						/>
					</Alert>
				)}

				{tile?.type === 'entry-door' && errorData && pageData.url.includes('fiberglass') && (!tile.price?.consumer?.amount || tile.price?.consumer?.amount === 0) && (
					<Alert
						className='mb-4 text-center'
						type='error'
						hideClose
					>
						<div
							dangerouslySetInnerHTML={{
								__html: errorData?.find((item) => item.non_field_id === 'availability bar')?.error,
							}}
						/>
					</Alert>
				)}

				{pageData.overview && (
					<div
						id='pdpHeroGrid'
						className={`${styles.pdpHero_Grid} flex flex-wrap pb-5`}
					>
						<div className={styles.pdpHero_ImageSliderWrapper}>
							<ImageSlider
								data={pageData.overview}
								productType={tile?.type}
							/>
						</div>

						<div className='basis-full md:flex-1 md:pl-6 xl:pl-10'>
							{pageData.overview.product_heading && (
								<h1
									className={`${styles.pdpHero_ProductTitle} mb-2 mt-4 text-2xl font-normal md:mt-0 xl:text-3xl`}
									dangerouslySetInnerHTML={{ __html: pageData.overview.product_heading }}
								/>
							)}

							{ratings?.productRatings?.OverallSatisfactionRating && ratings?.productRatings?.TotalReviews && (
								<div className={`${styles.pdpHero_Ratings} h-6 xl:mt-2`}>
									<StarRatings
										totalReviews={ratings.productRatings.TotalReviews}
										overallRating={ratings.productRatings.OverallSatisfactionRating}
										ratingsLink={pageData.modular_blocks.find((block) => block.reviews != null)?.reviews?.ctas}
										className='[&_span>span]:!text-3xl'
									/>
								</div>
							)}

							{pageData.flag && <p className={`${styles.pdpHero_Flag} mt-3 inline-flex bg-neutral-100 px-2.5 py-0.5 text-sm text-neutral-600`}>{pageData.flag}</p>}

							{(pageData.overview.description || tile?.mpn) && (
								<div className={`${styles.pdpHero_Content} mt-2.5`}>
									<div dangerouslySetInnerHTML={{ __html: pageData.overview.description }} />
									{tile?.mpn && (
										<ul>
											<li>Product #{tile.mpn}</li>
										</ul>
									)}
								</div>
							)}

							{isValidArray(pageData.overview?.alternative_products) && (
								<>
									{pageData.overview?.number_wide && (
										<div
											className='mt-5 xl:mt-8'
											dangerouslySetInnerHTML={{ __html: pageData.overview.number_wide }}
										/>
									)}

									<List
										bullets='none'
										className={`pdpHero-productBtns mt-3 flex flex-row flex-wrap gap-3 md:gap-4 xl:gap-6`}
									>
										{pageData.overview.alternative_products.map((product, index) => (
											<li
												key={index}
												className='flex-1 md:flex-[0_0_100px] xl:flex-[0_0_125px] [&_img]:h-[50px] [&_img]:!object-contain xl:[&_img]:h-[60px]'
											>
												<CTA
													data={product}
													className={`flex h-full flex-col px-2 pb-2 pt-4 text-center text-sm no-underline hover:decoration-primary hover:decoration-2 xl:text-sm [&>div]:!mb-1.5 [&>div]:flex-[0_0_60px] ${pageData.url === product.link?.href ? '!border-2 !border-black font-semibold shadow-[0_2px_4px_rgba(0,0,0,0.05)]' : '!border !border-neutral-200'}`}
												/>
											</li>
										))}
									</List>
								</>
							)}

							{pageData.overview?.subheading && <p className='text-subtitle-2 mt-4'>{pageData.overview.subheading}</p>}

							{isValidArray(pageData.overview?.variation) && (
								<div className='my-6 space-y-5'>
									{pageData.overview.variation.map((variation, index) => {
										if (!variation.badge || !variation.description) return null;

										return (
											<Collapse
												key={`variation-${index}`}
												className='!border-0'
												isOpen={pageData.overview.variation.length < 3}
											>
												<Collapse.Trigger
													className={`!px-4 !py-2 text-base [&>span>span]:!icon-heavy [&>span>span]:!text-3xl ${variation.badge.includes('Premium') ? 'bg-black text-white [&>span>span]:text-white' : variation.badge.includes('Standard') ? 'bg-neutral-50' : 'bg-primary text-black'}`}
													renderAs='div'
												>
													<span
														className='font-normal'
														dangerouslySetInnerHTML={{ __html: variation.badge }}
													/>
												</Collapse.Trigger>
												<Collapse.Content className='mt-2.5 !p-0'>
													<div
														className='text-body-2 [&>ul]:!m-0'
														dangerouslySetInnerHTML={{ __html: variation.description }}
													/>
												</Collapse.Content>
											</Collapse>
										);
									})}
								</div>
							)}

							<div className='mt-5 xl:mt-6'>
								{tile && (!isEcommerce || !tile.available_online || (!isPro && tile?.price?.consumer?.amount === 0)) && !configData?.nta_purchase_options?.backend_name?.find((name) => tile?.backend_name?.includes(name)) && (
									<>
										{pageData.pro && !isPro && configData?.stock?.find((item) => item.product_type === tile.backend_name)?.heading && <p className={`text-sm uppercase text-neutral`}>{configData.stock.find((item) => item.product_type === tile.backend_name).heading}</p>}
										<p className='my-2.5'>{configData?.pricing}</p>
									</>
								)}

								{(!tile?.available_online || tile?.price?.consumer?.amount === 0) && configData?.nta_purchase_options?.backend_name?.find((name) => tile?.backend_name?.includes(name)) && (
									<NonTransactableOptions
										productName={pageData.overview.product_heading}
										className='my-6'
									/>
								)}

								{isEcommerce && tile?.available_online && ((!isPro && tile?.price?.consumer?.amount !== 0) || isPro) && (
									<>
										{!configData?.stock?.find((item) => item.product_type === tile.backend_name)?.heading && configData?.exclusive_pricing?.heading && (
											<div className='mb-2 leading-tight'>
												<div className='inline-flex items-center md:relative'>
													<span className='bg-primary px-2 text-sm font-semibold uppercase text-black'>{isPro ? configData?.pro_price : configData?.online_price}</span>

													{configData?.exclusive_pricing?.description && (
														<button
															className='ml-2 block'
															id='monetate-price-info-btn'
															onClick={onPricingInfoClick}
															aria-label='More Info'
														>
															<Icon
																name='info'
																className='block text-base'
																style={{ fontVariationSettings: "'wght' 600" }}
															/>
														</button>
													)}

													{showPricingTooltip && (
														<div className={`${styles.pdpHero_Tooltip} fixed left-1/2 top-1/2 z-20 w-[270px] -translate-x-1/2 -translate-y-1/2 border border-black bg-white font-normal shadow-overlay md:absolute md:left-full md:top-0 md:w-[250px] md:translate-x-0 md:translate-y-0 md:p-2.5 md:shadow-lg`}>
															<div className='inline-flex w-full items-center justify-between border-b border-neutral-300 p-2.5 md:border-b-0 md:p-0'>
																<p className='tracking-0.5 text-xs uppercase'>{configData.exclusive_pricing.heading}</p>
																<button
																	className='md:hidden'
																	onClick={onPricingInfoClick}
																	aria-label='Pricing Info'
																>
																	<Icon
																		name='close'
																		className='block'
																	/>
																</button>
															</div>
															<div
																className={styles.pdpHero_TooltipContent}
																dangerouslySetInnerHTML={{ __html: configData.exclusive_pricing.description }}
															/>
														</div>
													)}
												</div>
											</div>
										)}

										{/* {configData?.stock?.find((item) => item.product_type === tile.backend_name)?.heading && <p className={`text-sm uppercase text-neutral`}>{configData.stock.find((item) => item.product_type === tile.backend_name).heading}</p>}
										{configData.stock?.find((item) => item.product_type === tile.backend_name)?.custom_starting == null && configData?.starting_at && <p className='mt-0 font-semibold'>{configData.starting_at}</p>}
										{configData.stock?.find((item) => item.product_type === tile.backend_name)?.custom_starting && <p className='mt-0 font-semibold'>{configData.stock.find((item) => item.product_type === tile.backend_name).custom_starting}</p>} */}
										<Price
											pricePrefix='From '
											price={isPro ? tile.price.professional : tile.price.consumer}
											priceComparison={isPro && tile.price.consumer?.amount !== 0 ? tile.price.consumer : null}
											className={`${styles.pdpHero_Price} mt-2px`}
										/>
									</>
								)}

								{isEcommerce && tile?.available_online && (
									<div className='mb-4 mt-2'>
										<PayPalMessages
											forceReRender={[tile?.price]}
											amount={isPro ? tile?.price?.professional?.amount / 100 : tile?.price?.consumer?.amount / 100}
											style={{
												logo: {
													type: 'inline',
												},
												text: {
													size: 16,
												},
											}}
											onClick={() => {
												window.dataLayer = window.dataLayer || [];
												window.dataLayer.push({
													event: 'paypal-click',
													paypal_page: 'pdp',
												});
											}}
										/>
									</div>
								)}

								{!configData?.nta_purchase_options?.backend_name?.find((name) => tile?.backend_name?.includes(name)) && (
									<List
										id='pdpBtnList'
										bullets='none'
										className={`${styles.pdpHero_Buttons} flex flex-row flex-wrap ${!isPro && !didSelectPro ? 'mt-8' : 'mt-2.5'}`}
									>
										{(!pageData.pro || (pageData.pro && isPro)) && (
											<>
												{tile && (tile.available_online || (!tile.available_online && !tile.manual)) && ((isEast && tile.east_only) || !tile.east_only) && tile.backend_name && (
													<li>
														<Button
															color='primary'
															id={isPartsOnly || (!tile.available_online && !tile.manual) ? 'speedcurve-build-and-add-to-project' : 'speedcurve-build-and-add-to-cart'}
															onClick={() => heroButtonClick(hasLocationPostalCode ? `/shop/configure/?id=${tile.backend_name}&type=${tile.type}&name=${productHeading}&heading=${tile.header} ${tile.display_name}&pdpURL=${typeof window != 'undefined' && window.location.href}&isPro=${isPro}` : false, isPartsOnly || (!tile.available_online && !tile.manual) ? 'project' : 'configure')}
														>
															{!isEcommerce || isProjectButton ? 'Build & Add to Project' : 'Build & Add to Cart'}
														</Button>
													</li>
												)}
												{isValidArray(configData.quote_ctas) && isValidArray(configData.quote_pro) && tile && !tile.backend_name?.includes('encompass') && configData.stock?.[0]?.product_type !== tile.backend_name && (
													<li>
														<CTA
															onClick={() => heroButtonClick(null, 'quote')}
															data={isPro || didSelectPro ? configData.quote_pro[0] : configData.quote_ctas[0]}
															id='monetate-pdp-get-quote-cta'
														/>
													</li>
												)}
												{isEcommerce && isEast && tile && configData.stock?.[0]?.product_type === tile.backend_name && configData.stock[0].ctas?.[0] && (
													<li>
														<CTA
															onClick={() => heroButtonClick(null, 'stock')}
															data={configData.stock[0].ctas[0]}
														/>
													</li>
												)}
												{isEcommerce && tile && !tile.available_online && tile.backend_name?.includes('encompass') && configData.contractor_ctas?.[0] && configData.lumberyard_ctas?.[0] && (
													<>
														<li>
															<CTA
																data={configData.contractor_ctas[0]}
																onClick={() => heroButtonClick(null, 'contractor')}
															/>
														</li>
														<li>
															<CTA
																data={configData.lumberyard_ctas[0]}
																onClick={() => heroButtonClick(null, 'lumberyard')}
															/>
														</li>
													</>
												)}
											</>
										)}
										{tile?.backend_name && pageData.pro && !isPro && configData?.stock?.find((item) => item.product_type === tile.backend_name) && (
											<>
												{configData?.stock?.find((item) => item.product_type === tile.backend_name)?.ctas?.[0] && (
													<li>
														<CTA
															data={configData.stock.find((item) => item.product_type === tile.backend_name).ctas[0]}
															state={{
																redirectURL: typeof window != 'undefined' && window.location.href,
															}}
															component={configData.stock.find((item) => item.product_type === tile.backend_name).ctas[0].gtm_link_type ? Link : null}
														/>
													</li>
												)}
												{configData?.stock?.find((item) => item.product_type === tile.backend_name)?.cta_2?.ctas?.[0] && (
													<li className={styles.pdpHero_StockItemWithHeading}>
														{configData.stock.find((item) => item.product_type === tile.backend_name).cta_2.heading && <p className={styles.pdpHero_StockBtnHeading}>{configData.stock.find((item) => item.product_type === tile.backend_name).cta_2.heading}</p>}
														<CTA
															data={configData.stock.find((item) => item.product_type === tile.backend_name).cta_2.ctas[0]}
															state={{
																redirectURL: typeof window != 'undefined' && window.location.href,
															}}
														>
															{configData.stock.find((item) => item.product_type === tile.backend_name).cta_2.ctas[0].link.title}
															<Icon name='keyboard_arrow_right' />
														</CTA>
													</li>
												)}
											</>
										)}
									</List>
								)}

								{tile?.analytics?.views > 499 && (
									<div className='mt-5 flex flex-row flex-wrap items-center xl:mt-6'>
										<div className='flex flex-initial flex-col items-center'>
											<Icon
												name='auto_awesome'
												className='text-sm/leading-none'
											/>
											<Icon name='shopping_cart' />
										</div>
										<p className='ml-3 flex-1 text-sm md:ml-4'>
											<span className='block font-semibold'>{configData?.building_text?.find((text) => text.product === tile.type)?.heading}</span>
											{configData?.building_text?.find((text) => text.product === tile.type)?.subheading} {tile.analytics.views} {configData?.building_text?.find((text) => text.product === tile.type)?.subheading2}
										</p>
									</div>
								)}

								{configData?.how_to_get_it && isEcommerce && tile?.available_online && ((isEast && tile.east_only) || !tile.east_only) && (
									<div className={styles.pdpHero_DeliveryWrapper}>
										{configData.how_to_get_it.heading && (
											<p className={styles.pdpHero_PriceTitleShipping}>
												{configData.how_to_get_it.heading}
												{addCity && addPostal && addState && add1 && (
													<span>
														for <button onClick={() => openAddressModal()}>{`${add1}, ${addCity}, ${addState} ${addPostal}`}</button>
													</span>
												)}
											</p>
										)}
										{isValidArray(configData?.how_to_get_it?.get_it_options) && (
											<>
												<Row
													noGutters={true}
													align='center'
													className={styles.pdpHero_DeliveryRow}
												>
													{configData.how_to_get_it.get_it_options.map((option, index) => (
														<Column
															xs={12}
															lg='fluid'
															key={index}
															className={index === 0 ? styles.pdpHero_DeliveryTitleBorderBot : ''}
														>
															<Row
																justify='center'
																// align='center'
																noGutters={true}
																className={!addCity || !addPostal || !addState || !add1 || addCity === '' || addPostal === '' || addState === '' || add1 === '' ? styles.pdpHero_DeliverySubRowMin : styles.pdpHero_DeliverySubRow}
															>
																<Column xs='auto'>
																	{option.file?.url && (
																		<img
																			src={option.file.url}
																			alt=''
																		/>
																	)}
																</Column>
																<Column xs='fluid'>
																	{option.heading && (
																		<p className={index === 1 ? styles.pdpHero_DeliveryTitle : styles.pdpHero_DeliveryTitleBorder}>
																			{option.heading}
																			<span className={submittedOnce ? (deliveryLocation?.price?.amount || pickupLocation?.dist?.miles?.value ? styles.pdpShippingAva : styles.pdpShippingNotAva) : ''} />
																		</p>
																	)}
																	{index == 0 && !deliveryError && deliveryLocation?.price && <p>Available</p>}
																	{index == 0 && !deliveryError && deliveryLocation?.price?.amount && (
																		<div className={styles.pdpHero_ShippingWrapper}>
																			<p className={styles.pdpHero_ShippingTitle}>{deliveryLocation.price.formatted}</p>
																			{deliveryLocation.price.formatted && index == 0 && (
																				<div className={styles.pdpHero_PriceInfo}>
																					<button
																						onClick={onShippingTooltipClick}
																						aria-label='Shipping Info'
																					>
																						<Icon name='info' />
																					</button>
																					{showShippingTooltip && index == 0 && (
																						<div className={styles.pdpHero_ShippingTooltip}>
																							{/* Needs to be added to CS */}
																							<div className={styles.pdpHero_ShippingTooltipContent}>This is the cost of delivery to your job site address for your entire order. The number of items in your order will not change this price. This fee may vary by address as it is set by your local retailer and based on distance and distribution costs.</div>
																						</div>
																					)}
																				</div>
																			)}
																		</div>
																	)}
																	{index == 1 && !pickupError && pickupLocation?.city && <p>Available at {pickupLocation.city}</p>}
																	{index == 1 && !pickupError && pickupLocation?.dist?.miles?.value && <p>{pickupLocation.dist.miles.value} miles from you</p>}
																	{submittedOnce && ((index == 0 && deliveryError) || (index == 1 && pickupError)) && <p>Currently unavailable</p>}
																</Column>
															</Row>
														</Column>
													))}
												</Row>
												<Row justify='center'>
													{(!addCity || !addPostal || !addState || !add1 || addCity === '' || addPostal === '' || addState === '' || add1 === '') && (
														<div className={styles.pdpAddress_link}>
															<span>
																<button onClick={() => openAddressModal()}>{'Enter your address to see available options'}</button>
															</span>
														</div>
													)}
												</Row>
											</>
										)}
									</div>
								)}
							</div>
						</div>
					</div>
				)}
			</Container>
			{isValidArray(pageData.modular_blocks) &&
				pageData.modular_blocks.map((block, index) => {
					if (isValidArray(block.cta_ribbon?.cta_ribbon)) {
						return (
							<Container key={index}>
								<CTARibbon data={block.cta_ribbon.cta_ribbon[0]} />
							</Container>
						);
					}

					if (isValidArray(block.adm_product?.adm_product)) {
						return (
							<div
								key={index}
								className='py-6 md:py-8 xl:py-12'
							>
								<Container>
									{block.adm_product.heading && (
										<h2
											className='text-h3 mb-4'
											dangerouslySetInnerHTML={{ __html: block.adm_product.heading }}
										/>
									)}
									<ADMProduct
										data={block.adm_product.adm_product[0]}
										docsOnly={block.adm_product.documents_only}
										defaultOpen={true}
									/>
								</Container>
							</div>
						);
					}

					if (block.pro_bullets != null) {
						return (
							<div
								id={block.pro_bullets.anchor_text}
								key={index}
								className='my-10'
							>
								<Container>
									<Row align='center'>
										{isValidArray(block?.pro_bullets?.reference) && (
											<Column
												xs={12}
												sm={5}
												xl={4}
											>
												<Carousel
													className='text-center'
													showArrows
												>
													{block.pro_bullets.reference.map((image, index) => (
														<div key={index}>
															<Image
																data={image}
																className='mx-auto block'
																objectFit='contain'
															/>
														</div>
													))}
												</Carousel>
											</Column>
										)}
										<Column
											xs={12}
											sm={7}
											xl='fluid'
										>
											<div className='mt-6 md:mt-0 md:pl-8'>
												{block.pro_bullets.heading && <p className='mb-5 text-lg font-semibold uppercase lg:text-2xl'>{block.pro_bullets.heading}</p>}
												{block.pro_bullets.description && (
													<div
														dangerouslySetInnerHTML={{ __html: block.pro_bullets.description }}
														className={styles.pdpProBullets_Description}
													/>
												)}
											</div>
										</Column>
									</Row>
								</Container>
							</div>
						);
					}

					if (isValidArray(block.styles_table?.table)) {
						return (
							<div
								key={index}
								id={block.styles_table.anchor_text}
							>
								<Container>
									<Row className='max-md:mx-0 max-md:[&>*]:px-0'>
										{block.styles_table.table.map((table, tableIndex) => (
											<Column
												xs={12}
												xl={6}
												key={`table-${tableIndex}`}
											>
												<table className={`w-full border border-t-2 border-neutral-200 border-t-primary ${tableIndex % 2 !== 0 ? 'max-xl:border-t-0' : 'max-xl:border-b-0'}`}>
													<tbody>
														{isValidArray(table.row) &&
															table.row.map((row, rowIndex) => (
																<tr
																	className={`align-top ${tableIndex % 2 !== 0 ? (rowIndex % 2 === 0 ? 'bg-neutral-25' : 'bg-white') : rowIndex % 2 === 0 ? 'bg-white' : 'bg-neutral-25'}`}
																	key={`row-${rowIndex}`}
																>
																	{row.row_heading && <td className='block border-b-neutral-200 px-6 pb-2 pt-4 text-sm font-semibold md:table-cell md:w-[100px] md:border-b md:p-4 md:text-base md:font-normal'>{row.row_heading}</td>}

																	<td className='block space-y-2.5 border-b border-b-neutral-200 px-6 pb-4 md:table-cell md:p-4'>
																		{isValidArray(row.data) &&
																			row.data.map((data, dataIndex) => (
																				<div
																					key={`data-${dataIndex}`}
																					className={`flex ${data.badge_alignment === 'aside' ? 'flex-row-reverse gap-x-2' : 'flex-col'}`}
																				>
																					{data.badge && (
																						<div className='flex-none'>
																							<Badge
																								size='md'
																								color={data.color}
																								dangerouslySetInnerHTML={{ __html: data.badge }}
																								className='mb-1 !border-0 !px-1 !pb-1'
																							/>
																						</div>
																					)}

																					{data.content && (
																						<div
																							className='flex-1 text-sm md:text-base [&_ul]:m-0 [&_ul]:pl-6'
																							dangerouslySetInnerHTML={{ __html: data.content }}
																						/>
																					)}
																				</div>
																			))}
																	</td>
																</tr>
															))}
													</tbody>
												</table>
											</Column>
										))}
									</Row>
								</Container>
							</div>
						);
					}

					if (isValidArray(block.data_table?.table)) {
						return (
							<div
								key={index}
								className={styles.pdpDataTable}
							>
								<Container>
									<Row className='max-md:mx-0 max-md:[&>*]:px-0'>
										{block.data_table.table.map((table, index) => (
											<Column
												xs={12}
												xl={6}
												key={index}
											>
												<table className={`${styles.pdpDataTable_Table} ${index % 2 ? styles.pdpDataTable_Table_alt : ''}`}>
													<tbody>
														{isValidArray(table?.row) &&
															table.row.map((row, index) => (
																<tr
																	className='align-top'
																	key={index}
																>
																	{row.row_heading && <td>{row.row_heading}</td>}
																	{row.data && (
																		// eslint-disable-next-line jsx-a11y/control-has-associated-label -- Unclear why in github pr this is getting hit here
																		<td
																			dangerouslySetInnerHTML={{
																				__html: row.data,
																			}}
																		/>
																	)}
																</tr>
															))}
													</tbody>
												</table>
											</Column>
										))}
									</Row>
								</Container>
							</div>
						);
					}

					if (block.image_aside != null && block.image_aside.callout) {
						return (
							<ImageBanner
								data={block.image_aside}
								key={index}
							/>
						);
					}

					if (block.image_aside != null && !block.image_aside.callout) {
						return (
							<Feature
								data={block.image_aside}
								key={index}
							/>
						);
					}

					if (block.features_and_options != null) {
						return (
							<PDPTabs
								key={index}
								className='bg-neutral-50 py-12'
								data={block.features_and_options}
								proIcon={configData?.tips_for_pros?.url}
								configUrl={CONFIG_URL}
								pageUrl={pageData.url}
							/>
						);
					}

					if (isValidArray(block.faq?.group) && checkDynamic.faq != false) {
						return (
							<div
								key={index}
								id={block.faq.anchor_text || null}
								className={styles.pdpFaq}
							>
								<Container>
									{block.faq.heading && <h2 className={`${styles.pdpFaq_Title} s2-h2__alt`}>{block.faq.heading}</h2>}

									<div className={`${styles.pdpFaq_Columns} ${block.faq.group.length == 1 ? styles.pdpFaq_Columns_single : ''}`}>
										{block.faq.group.map((group, index) => (
											<React.Fragment key={index}>
												{isValidArray(group?.faq) && (
													<div>
														{group.heading && <p className={styles.pdpFaq_Heading}>{group.heading}</p>}
														<FAQ
															data={group.faq}
															key={index}
															noStructuredData={true}
															multipleColumns={block.faq.group.length == 1}
														/>
													</div>
												)}
											</React.Fragment>
										))}
									</div>
									{block.faq.ctas?.[0] && (
										<div className='mt-4 text-center xl:mt-6'>
											<CTA
												data={block.faq.ctas[0]}
												renderAs='link'
												className='text-sm font-semibold uppercase tracking-wide'
											/>
										</div>
									)}
								</Container>
							</div>
						);
					}

					if (block.reviews != null && ratings) {
						return (
							<ReviewsSlider
								key={index}
								id={block.reviews.anchor_text}
								reviews={ratings}
								title={block.reviews.heading}
								cta={block.reviews.ctas}
							/>
						);
					}

					if (isValidArray(block.compare?.products)) {
						return (
							<EcomCardSlider
								data={block.compare}
								dynamic={{ manual_entries: block.compare.products }}
								key={index}
							/>
						);
					}

					if (isValidArray(block.all_dynamic?.dynamic)) {
						return (
							<DynamicComponent
								data={block.all_dynamic}
								key={index}
								parentURL={pageData.url}
								checkDynamic={checkDynamicPanels}
							/>
						);
					}

					if (block.cards != null) {
						return (
							<CardSection
								data={block.cards}
								key={index}
								className={styles[`cards${index}`]}
								pdpUrl={pageData.url}
							/>
						);
					}

					if (block.embed != null) {
						return (
							<div
								key={index}
								id={block.embed.anchor_text}
								className='mt-6 md:mt-8 xl:mt-12'
							>
								<HoverEmbed configId={block.embed.embed_id} />
							</div>
						);
					}
				})}

			<Modal
				isOpen={modalOpen}
				onClose={closeModal}
				width={800}
			>
				<Modal.Header>{isEcommerce ? locationConfig.build_location.heading : locationConfig.non_us_location.heading}</Modal.Header>
				<Modal.Content>
					{modalHasError && (
						<Alert
							type='error'
							onClose={closeErrorAlert}
							className='mb-4'
						>
							<div className={styles.forgotPassword_alert}>{modalErrorMessage}</div>
						</Alert>
					)}

					<div
						className='mb-5'
						dangerouslySetInnerHTML={{
							__html: isEcommerce ? locationConfig.build_location.description : locationConfig.non_us_location.description,
						}}
					/>

					<form onSubmit={confirmZIPCode}>
						<div className='flex flex-row flex-wrap items-end'>
							<TextInput
								id='zipCode'
								labelText='Zip Code'
								placeholder='ZIP Code'
								autoComplete='off'
								onChange={handleChange}
								groupClass='!m-0 w-full sm:w-auto'
							/>
							<Button
								id='speedcurve-zip-confirm'
								className='mt-2.5 sm:ml-2.5 sm:mt-0 sm:h-[54px] sm:py-1'
								type='submit'
							>
								Confirm Address
							</Button>
						</div>
					</form>
				</Modal.Content>
			</Modal>

			<Modal
				isOpen={addressModalOpen}
				onClose={() => closeAddressModal()}
			>
				<Modal.Content className={styles.phpAddressModal}>
					<div className={styles.pdpAddressModalHeader}>
						{modalHasError && (
							<Alert
								type='error'
								onClose={closeErrorAlert}
							>
								<div className={styles.forgotPassword_alert}>{modalErrorMessage}</div>
							</Alert>
						)}
						<h1>{configData?.how_to_get_it?.modal?.heading}</h1>
						<p>{configData?.how_to_get_it?.modal?.subheading}</p>
					</div>
					<div className={styles.pdpAddressModalContent}>
						<div>{configData?.how_to_get_it.modal?.job_site}</div>
						<div className={styles.pdpAddModalForm}>
							<div className={styles.pdpAddModalFormGroup}>
								<AddressValidation
									allowInternational={false}
									defaultedPostalCode={postalCode}
									addressComplete={[addressComplete, setAddressComplete]}
									addressInputs={{
										line1: { label: 'Street Address', xs: 12 },
										line2: { visible: false },
										city: { xs: 12, sm: 6, lg: 4 },
										state: { xs: 12, sm: 6, lg: 4 },
										postalCode: { xs: 12, sm: 6, lg: 4 },
									}}
									addressInput={customerRecord}
									addressOutput={[addressOutput, setAddressOutput]}
									addressModified={[addressModified, setAddressModified]}
									className={'addressValidation_fulfillment'}
								/>
							</div>
						</div>
					</div>
					<div className={'my-5 ml-2.5 mr-2.5 flex flex-wrap justify-end xl:mr-2.5'}>
						<Button
							className={styles.pdpAddressCancelModalButton}
							onClick={() => closeAddressModal()}
							outline={true}
						>
							Cancel
						</Button>
						<Button
							className={'mx-0 mt-2 sm:mx-2.5 sm:mt-0'}
							onClick={() => submitAddress()}
						>
							Update Address
						</Button>
					</div>
				</Modal.Content>
			</Modal>

			{tile && pageData && (ratings?.productRatings?.OverallSatisfactionRating || reviews.Empty || !pageData?.product_tile?.[0]?.ratings?.[0]?.ratings_code) && (
				<Product
					product={tile}
					name={productHeading}
					description={pageData.seo.description}
					images={getImageUrls()}
					ratingValue={ratings?.productRatings?.OverallSatisfactionRating}
					ratingCount={ratings?.productRatings?.TotalReviews}
					customerReviews={isValidArray(reviews?.Reviews) ? reviews.Reviews.filter((rev) => rev.OverallSatisfactionRating > 4) : []}
					productUrl={pageData.url}
				/>
			)}
		</Layout>
	);
};

PDPStatic.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
	postalCode: PropTypes.string,
	deliveryLocation: PropTypes.array,
	pickupLocation: PropTypes.array,
	deliveryMethod: PropTypes.array,
	customerRecord: PropTypes.array,
	customer: PropTypes.object,
	setErrors: PropTypes.func,
};

export default PDPStatic;

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => (
	<SEO
		// eslint-disable-next-line react/prop-types -- fix this
		{...data.contentstackStaticPdp.seo}
	/>
);

export const query = graphql`
	query ($uid: String!, $backend_name: String) {
		contentstackStaticPdp(id: { eq: $uid }) {
			url
			url_canonical
			breadcrumb {
				active_page
				back_pages {
					...CTA
				}
			}
			pro
			product_tile {
				...Tile
			}
			has_ar
			ar_text
			flag
			overview {
				product_heading
				description
				cob {
					...Image
					image {
						gatsbyImageData(height: 675, quality: 90)
					}
				}
				aug_3d {
					url
				}
				beauty_images {
					...Image
					image {
						gatsbyImageData(height: 675, quality: 90)
					}
				}
				number_wide
				alternative_products {
					...CTA
				}
				subheading
				variation {
					badge
					description
				}
			}
			modular_blocks {
				cta_ribbon {
					cta_ribbon {
						...CTARibbon
					}
				}
				pro_bullets {
					anchor_text
					reference {
						...Image
						image {
							gatsbyImageData(width: 400, quality: 90)
						}
					}
					heading
					description
				}
				data_table {
					table {
						row {
							row_heading
							data
						}
					}
				}
				styles_table {
					anchor_text
					table {
						row {
							row_heading
							data {
								content
								badge_alignment
								badge
								color
							}
						}
					}
				}
				image_aside {
					...ImageAside
				}
				features_and_options {
					anchor_text
					heading
					options_and_swatches {
						tabs {
							tab_name
							hardware_tabs
							tab_content
							swatches {
								...FNOOption
							}
						}
					}
				}
				faq {
					anchor_text
					heading
					group {
						heading
						faq {
							question
							answer
						}
					}
					ctas {
						...CTA
					}
				}
				reviews {
					anchor_text
					heading
					ctas {
						...CTA
					}
				}
				compare {
					heading
					products {
						... on Contentstack_tile {
							...Tile
						}
					}
				}
				all_dynamic {
					heading
					dynamic {
						...Dynamic
					}
				}
				cards {
					...CardSection
				}
				adm_product {
					documents_only
					heading
					adm_product {
						...ADMProduct
					}
				}
				embed {
					anchor_text
					embed_id
				}
			}
			seo {
				title
				description
				noIndex: no_index_page
				page_type
			}
		}
		contentstackLocationConfig {
			build_location {
				box_heading
				description
				heading
			}
			non_us_location {
				box_heading
				description
				heading
			}
		}
		productTiles(backend_name: { eq: $backend_name }) {
			...ProductTile
		}
	}
`;
